import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Tooltip } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { ImportRowExclusionHandlers } from '../../importRows';
import { RowExclusionLockContext } from './rowExclusionLock';

type RowExclusionButtonProps = ImportRowExclusionHandlers & {
    rowNumber: number;
    isExcluded: boolean;
};

/**
 * A row's one control, in the table's last column and at the top of a phone
 * card: Exclude, or Include on an excluded row. A ghost icon-only button (a
 * circle with a minus, or an undo arrow) with a tooltip; it writes the
 * store's exclusions and sends nothing. While the import runs
 * (`RowExclusionLockContext`, provided by the table and the card list) it
 * waits.
 *
 * The tooltip's trigger drops an `aria-label`, so the button is named by a
 * visually hidden span that also names the row ("Exclude row 902"; the chart
 * of accounts' pattern).
 */
export const RowExclusionButton: React.FC<RowExclusionButtonProps> = ({
    rowNumber,
    isExcluded,
    onExcludeRow,
    onIncludeRow,
}) => {
    const { t } = useTranslation('pages');
    const isLocked = React.useContext(RowExclusionLockContext);

    return (
        <Tooltip delay={0}>
            <Button
                isIconOnly
                data-excluded={isExcluded}
                isDisabled={isLocked}
                onPress={() =>
                    isExcluded
                        ? onIncludeRow(rowNumber)
                        : onExcludeRow(rowNumber)
                }
                size="sm"
                variant="ghost"
                {...getNodeTestIdProp(
                    `catalog-import-row-exclusion-${rowNumber}`,
                )}
            >
                <Icon
                    aria-hidden
                    icon={
                        isExcluded
                            ? 'gravity-ui:arrow-rotate-left'
                            : 'gravity-ui:circle-minus'
                    }
                />
                <span className="sr-only">
                    {t(
                        isExcluded
                            ? 'catalogImport.products.rowAction.includeRow'
                            : 'catalogImport.products.rowAction.excludeRow',
                        { row: rowNumber },
                    )}
                </span>
            </Button>
            <Tooltip.Content>
                <p>
                    {t(
                        isExcluded
                            ? 'catalogImport.products.rowAction.include'
                            : 'catalogImport.products.rowAction.exclude',
                    )}
                </p>
            </Tooltip.Content>
        </Tooltip>
    );
};
